import { useState, useMemo } from 'react';
import { Tooltip } from 'components/common';
import { Input } from 'antd';
import { CheckOutlined, UndoOutlined, CloseOutlined } from '@ant-design/icons';
import { revertableInput as strings } from '../strings';
import * as SC from '../styled';

/**
 * A specialised Input that allows a user to revert changes to a pre-specified value.
 * @param {Object} props
 * @param {string} props.revertValue When the user reverts the changes, what should the value be?
 * @param {string} props.currentValue The starting value of the input.
 * @param {function(string): void} props.onNewValueConfirmed Notifies the parent that the value was confirmed.
 * @param {function(string): string|null} [props.validate] Optional validation function that returns error message or null
 */
export const RevertableInput = ({
  revertValue,
  currentValue,
  onNewValueConfirmed,
  validate,
}) => {
  const [value, setValue] = useState(
    currentValue !== revertValue ? currentValue : ''
  );

  const validationError = useMemo(() => (validate ? validate(value) : null), [
    value,
    validate,
  ]);
  const isValid = validationError === null;

  const handleConfirm = () => {
    if (!isValid) return;
    onNewValueConfirmed(value.trim());
  };

  const handleRevert = () => onNewValueConfirmed(revertValue);

  const handleChange = e => setValue(e.target.value);

  return (
    <SC.RevertableInputRoot>
      <div style={{ display: 'flex' }}>
        {/* Suppress password-manager autofill — SCIL-15783 */}
        <Input
          size='small'
          value={value}
          onChange={handleChange}
          onPressEnter={handleConfirm}
          autoFocus
          placeholder={revertValue}
          status={!isValid ? 'error' : ''}
          autoComplete='off'
          name='export-template-rename'
          data-1p-ignore
          data-lpignore='true'
          suffix={
            !!value && (
              <Tooltip title={strings.revertTooltip(revertValue)}>
                <SC.RevertableInputRevertButton onClick={handleRevert}>
                  <UndoOutlined />
                </SC.RevertableInputRevertButton>
              </Tooltip>
            )
          }
        />
        {!!value && (
          <Tooltip title={strings.confirmTooltip(value)}>
            <SC.RevertableInputCheckButton
              onClick={handleConfirm}
              disabled={!isValid}
            >
              <CheckOutlined />
            </SC.RevertableInputCheckButton>
          </Tooltip>
        )}
        {!value && (
          <Tooltip title={strings.cancelTooltip}>
            <SC.RevertableInputCheckButton
              onClick={handleConfirm}
              disabled={!isValid}
            >
              <CloseOutlined style={{ color: '#aaa' }} />
            </SC.RevertableInputCheckButton>
          </Tooltip>
        )}
      </div>
      {!isValid && <SC.ValidationError>{validationError}</SC.ValidationError>}
    </SC.RevertableInputRoot>
  );
};
